import { useEffect } from 'react'
import { getUser } from '../../../assets/requests'
import { useClerk } from '@clerk/clerk-react'

/**
 * Custom hook for managing Canva integration logic
 * Extracted from auth.js to handle Canva user sync and parameter processing
 *
 * Handles Canva authentication flow by checking if the user's Kinstak record
 * is synced with their Canva ID. If synced, redirects to Canva success URL.
 * If not synced or no user in local storage, forces user to login.
 */
export const useCanvaIntegration = () => {

  const { signOut } = useClerk()

  /**
   * Extract Canva-related parameters from URL
   * @returns {Object} Object containing Canva parameters
   */
  const getCanvaParameters = () => {
    const inParams = new URLSearchParams(window.location.search)

    return {
      isCanvaUser: inParams.get('canva_user') === 'true',
      kinstakUserId: localStorage.getItem('username'),
      canvaUserId: inParams.get('userId'),
      state: inParams.get('state'),
      urlParams: inParams
    }
  }

  /**
   * Redirect to Canva success URL with appropriate parameters
   * @param {string} apiGatewayUrl - API Gateway base URL
   * @param {string} state - State parameter from Canva
   * @param {string} canvaUserId - Canva user ID
   * @param {string} kinstakUserId - Kinstak user ID
   */
  const redirectToCanvaSuccess = (
    apiGatewayUrl,
    state,
    canvaUserId,
    kinstakUserId
  ) => {
    const params = new URLSearchParams({
      state: state,
      canvaUserId: canvaUserId,
      userId: kinstakUserId
    })

    const successUrl = `${apiGatewayUrl}/k1/canva/success?${params.toString()}`
    window.location.replace(successUrl)
  }

  /**
   * Confirm that the Kinstak user is synced with their Canva ID
   * If synced, redirect to Canva success URL
   * If not synced, force user to login form
   * @param {Object} canvaParams - Canva parameters object
   */
  const confirmUserSync = async canvaParams => {
    const { kinstakUserId, canvaUserId, state } = canvaParams
    const API_GATEWAY_URL = process.env.REACT_APP_API_GATEWAY_URL

    let user
    try {
      user = await getUser(kinstakUserId)
    } catch (err) {
      console.log('Error getting Kinstak user data:', err)
      return
    }

    const userSynced = user?.externalAccounts?.canvaId === canvaUserId

    if (userSynced) {
      console.log('Kinstak user record is synced with Canva')
      redirectToCanvaSuccess(API_GATEWAY_URL, state, canvaUserId, kinstakUserId)
    } else {
      console.log('Kinstak user record is NOT synced with Canva')
      // Force the user to the login form
      // (this Clerk call handles post-signout navigation)
      await signOut()
    }
  }

  useEffect(() => {
    // Initialize Canva user sync check based on URL parameters
    const initializeCanvaSync = async () => {
      const canvaParams = getCanvaParameters()

      if (canvaParams.isCanvaUser && canvaParams.kinstakUserId !== 'null') {
        await confirmUserSync(canvaParams)
      }
    }

    // Initialize Canva user sync check on component mount
    initializeCanvaSync()
    // ESLint disabled: confirmUserSync depends on getCanvaParameters which doesn't need to be in deps
    // as the function is stable and doesn't depend on props or state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Check if current request is part of Canva authentication flow
   * @returns {boolean} True if this is a Canva authentication request
   */
  const isCanvaAuthFlow = () => {
    const canvaParams = getCanvaParameters()
    return canvaParams.isCanvaUser
  }

  return {
    getCanvaParameters,
    confirmUserSync,
    redirectToCanvaSuccess,
    isCanvaAuthFlow
  }
}

export default useCanvaIntegration
